import { useState } from "react";
import { Gauge, Radio, Rows3, TrendingUp } from "lucide-react";
import {
  getAdminTradovateEndpointBreakdown,
  useQuery,
} from "wasp/client/operations";
import { Card, CardContent } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";

// Même défaut que la courbe de quota affichée juste au-dessus : les deux
// cartes se lisent ensemble, et des fenêtres différentes rendraient leurs
// chiffres incomparables sans que rien ne le signale.
const DEFAULT_HOURS = 48;

type AllowedHours = 24 | 48 | 168 | 720;

/** Barre de part, sans librairie : une div dont la largeur est le pourcentage. */
const ShareBar = ({ share }: { share: number }) => (
  <div className="bg-accent h-1.5 w-full overflow-hidden rounded-full">
    <div
      className="bg-primary h-full rounded-full"
      style={{ width: `${Math.min(share, 100)}%` }}
    />
  </div>
);

const TradovateEndpointBreakdownCard = () => {
  const t = useTranslation();
  const [hours, setHours] = useState<AllowedHours>(DEFAULT_HOURS);
  const { data } = useQuery(getAdminTradovateEndpointBreakdown, {
    hours,
  });

  const ranges: { value: AllowedHours; label: string }[] = [
    { value: 24, label: t.adminDashboard.quotaHistoryRange24h },
    { value: 48, label: t.adminDashboard.quotaHistoryRange48h },
    { value: 168, label: t.adminDashboard.quotaHistoryRange7d },
    { value: 720, label: t.adminDashboard.quotaHistoryRange30d },
  ];

  const endpoints = data?.endpoints ?? [];
  const users = data?.users ?? [];
  const projections = data?.projections ?? [];
  const totalWsEvents = data?.totalWsEvents ?? 0;
  const hasBetaTesters = users.some((u) => u.isBetaTester);
  // Retypage a la frontiere, comme pour les tableaux ci-dessous : SuperJSON
  // elargit `string | null` a toute sa union de valeurs serialisables, que
  // React refuse de rendre.
  const comparisons = (data?.tradeComparisons ?? []) as Array<
    Record<string, string | number | boolean | null>
  >;
  // Ecritures REELLES du socket. Retypage a la frontiere, comme le reste :
  // SuperJSON elargit `number` a toute sa union de valeurs serialisables.
  const wsWritten = Number(data?.wsWrittenTrades ?? 0);
  const wsWriteFailures = Number(data?.wsWriteFailures ?? 0);
  // Trous du socket réparés par le contrôle quotidien (`source = 'cycle'`).
  const wsRepaired =
    data?.wsRepairedByControl === null || data?.wsRepairedByControl === undefined
      ? null
      : Number(data.wsRepairedByControl);
  // Modèle de coût mesuré : fixe par connexion + variable par compte.
  const fixedPerConnection =
    data?.fixedCallsPerConnectionHour == null ? null : Number(data.fixedCallsPerConnectionHour);
  const variablePerAccount =
    data?.variableCallsPerAccountHour == null ? null : Number(data.variableCallsPerAccountHour);

  return (
    <div>
      <h2 className="text-foreground mb-1 flex items-center gap-2 text-lg font-semibold">
        <Gauge className="h-4 w-4" />
        {t.adminDashboard.endpointBreakdownTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.endpointBreakdownHint}
      </p>

      <Card>
        <CardContent className="space-y-6 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                {t.adminDashboard.endpointBreakdownCallsCol}{" "}
                <span className="text-foreground font-mono">
                  {data?.totalCalls ?? 0}
                </span>
              </span>
              <span className="text-muted-foreground">
                {t.adminDashboard.endpointBreakdownMedianLabel}{" "}
                <span className="text-foreground font-mono">
                  {data?.medianCallsPerAccountPerHour ?? "-"}
                </span>
              </span>
              {/* L'écart total/ventilé n'est affiché QUE s'il existe : le
                  masquer ferait douter des deux chiffres, mais l'afficher à
                  zéro ajouterait du bruit permanent. */}
              {(data?.unattributedCalls ?? 0) > 0 && (
                <span
                  className="text-muted-foreground"
                  title={t.adminDashboard.endpointBreakdownUnattributedHint}
                >
                  {t.adminDashboard.endpointBreakdownUnattributed}{" "}
                  <span className="text-foreground font-mono">
                    {data?.unattributedCalls}
                  </span>
                </span>
              )}
            </div>

            <div className="flex gap-1">
              {ranges.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setHours(r.value)}
                  className={`rounded px-2 py-1 text-xs transition-colors ${
                    hours === r.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-accent"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* --- Ventilation par endpoint --- */}
          {endpoints.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {t.adminDashboard.endpointBreakdownEmpty}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-left text-xs">
                    <th className="pb-2 font-normal">
                      {t.adminDashboard.endpointBreakdownEndpointCol}
                    </th>
                    <th className="pb-2 text-right font-normal">
                      {t.adminDashboard.endpointBreakdownCallsCol}
                    </th>
                    <th className="pb-2 text-right font-normal">
                      {t.adminDashboard.endpointBreakdownPerHourCol}
                    </th>
                    <th className="w-1/4 pb-2 pl-4 font-normal">
                      {t.adminDashboard.endpointBreakdownShareCol}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {endpoints.map((e) => (
                    <tr key={e.endpoint} className="border-border/50 border-b">
                      <td className="text-foreground py-2 font-mono text-xs">
                        {e.endpoint}
                      </td>
                      <td className="text-foreground py-2 text-right font-mono">
                        {e.calls}
                      </td>
                      <td className="text-muted-foreground py-2 text-right font-mono">
                        {e.callsPerHour}
                      </td>
                      <td className="py-2 pl-4">
                        <div className="flex items-center gap-2">
                          <ShareBar share={e.share} />
                          <span className="text-muted-foreground w-12 shrink-0 text-right font-mono text-xs">
                            {e.share}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* --- Par utilisateur : le ratio qui rend les profils comparables --- */}
          {users.length > 0 && (
            <div>
              <h3 className="text-foreground mb-1 text-sm font-semibold">
                {t.adminDashboard.endpointBreakdownUsersTitle}
              </h3>
              <p className="text-muted-foreground mb-3 text-xs">
                {t.adminDashboard.endpointBreakdownRatioHint}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-left text-xs">
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.endpointBreakdownUserCol}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.endpointBreakdownAccountsCol}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.endpointBreakdownCallsCol}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        {t.adminDashboard.endpointBreakdownRatioCol}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.userId} className="border-border/50 border-b">
                        <td className="text-foreground py-2">
                          <span className="flex items-center gap-2">
                            <span className="truncate">{u.userEmail}</span>
                          </span>
                        </td>
                        <td className="text-muted-foreground py-2 text-right font-mono">
                          {u.activeAccounts}
                        </td>
                        <td className="text-foreground py-2 text-right font-mono">
                          {u.calls}
                        </td>
                        <td className="text-foreground py-2 text-right font-mono font-semibold">
                          {u.callsPerAccountPerHour ?? "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* --- Projection : le chiffre qui répond à « le B2B tient ? » --- */}
          <div>
            <h3 className="text-foreground mb-1 flex items-center gap-2 text-sm font-semibold">
              <TrendingUp className="h-3.5 w-3.5" />
              {t.adminDashboard.endpointBreakdownProjectionTitle}
            </h3>
            <p className="text-muted-foreground mb-3 text-xs">
              {t.adminDashboard.endpointBreakdownProjectionHint}
            </p>
            {/* Le modèle qui fonde la projection, affiché pour qu'on puisse
                la refaire à la main sur un scénario de deal. */}
            {fixedPerConnection !== null && variablePerAccount !== null && (
              <p className="text-muted-foreground mb-3 text-xs">
                {t.adminDashboard.endpointBreakdownCostModel}{" "}
                <span className="text-foreground font-mono">{fixedPerConnection}</span>{" "}
                {t.adminDashboard.endpointBreakdownCostPerConnection}{" "}
                <span className="text-foreground font-mono">{variablePerAccount}</span>{" "}
                {t.adminDashboard.endpointBreakdownCostPerAccount}
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {projections.map((p) => {
                // Sans ratio mesuré on n'affiche NI vert NI rouge : un verdict
                // sans donnée serait le pire des messages sur une carte censée
                // décider d'un deal.
                const unknown =
                  fixedPerConnection === null &&
                  (data?.medianCallsPerAccountPerHour ?? null) === null;
                return (
                  <div
                    key={p.accounts}
                    className="border-border rounded-lg border p-3"
                  >
                    <div className="text-muted-foreground text-xs">
                      {p.accounts}{" "}
                      {t.adminDashboard.endpointBreakdownProjectionAccounts}
                      {p.connections !== null && (
                        <>
                          {" · "}
                          <span className="font-mono">{String(p.connections)}</span>{" "}
                          {t.adminDashboard.endpointBreakdownProjectionConnections}
                        </>
                      )}
                    </div>
                    <div className="text-foreground mt-1 font-mono text-2xl">
                      {unknown ? "-" : p.projectedCallsPerHour}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {t.adminDashboard.endpointBreakdownProjectionCalls} /{" "}
                      <span className="font-mono">{p.quota}</span>
                    </div>
                    <div
                      className={`mt-2 text-xs ${
                        unknown
                          ? "text-muted-foreground"
                          : p.withinQuota
                            ? "text-pnlPositive"
                            : "text-pnlNegative"
                      }`}
                    >
                      {unknown
                        ? t.adminDashboard.endpointBreakdownProjectionUnknown
                        : p.withinQuota
                          ? t.adminDashboard.endpointBreakdownProjectionWithin
                          : t.adminDashboard.endpointBreakdownProjectionOver}
                    </div>

                    {/* LE chiffre qui repond a « est-ce que le deal B2B
                        tient ? » : la meme projection, une fois la bascule
                        faite. Masquee sans mesure plutot qu'affichee a zero. */}
                    {!unknown && p.projectedCallsPerHourAfterWs !== null && (
                      <div className="border-border/50 mt-2 border-t pt-2">
                        <div className="text-muted-foreground text-[10px]">
                          {t.adminDashboard.endpointBreakdownAfterWs}
                        </div>
                        <div
                          className={`font-mono text-lg ${
                            p.withinQuotaAfterWs
                              ? "text-pnlPositive"
                              : "text-pnlNegative"
                          }`}
                        >
                          {String(p.projectedCallsPerHourAfterWs)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* --- Comparaison trade par trade : la preuve de non-derive --- */}
          <div>
            {/* VERDICT D'ECRITURE, en tete de la comparaison.
                La vue croisee ci-dessous dit ce que chaque flux VOIT, jamais
                qui a ECRIT : en mode WS seul le REST voit toujours les trades
                sans les ecrire, donc « Concordant » ne prouve rien sur
                l'origine. Ce bandeau est le seul endroit qui y repond. */}
            <div className="border-border/50 mb-4 rounded border p-3">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-muted-foreground">
                  {t.adminDashboard.wsWrittenLabel}{" "}
                  <span
                    className={
                      wsWritten > 0
                        ? "text-pnlPositive font-mono text-base font-semibold"
                        : "text-muted-foreground font-mono text-base font-semibold"
                    }
                  >
                    {wsWritten}
                  </span>
                </span>
                {/* Les echecs ne sont affiches QUE s'il y en a : un « 0 »
                    permanent ajouterait du bruit a un ecran deja dense. */}
                {wsWriteFailures > 0 && (
                  <span className="text-muted-foreground">
                    {t.adminDashboard.wsWriteFailuresLabel}{" "}
                    <span className="text-pnlNegative font-mono font-semibold">
                      {wsWriteFailures}
                    </span>
                  </span>
                )}
                {/* Un trou réparé est un trade que le socket avait manqué :
                    rouge dès le premier. */}
                {wsRepaired !== null && (
                  <span className="text-muted-foreground">
                    {t.adminDashboard.wsRepairedLabel}{" "}
                    <span
                      className={`font-mono font-semibold ${
                        wsRepaired > 0 ? "text-pnlNegative" : "text-foreground"
                      }`}
                    >
                      {wsRepaired}
                    </span>
                  </span>
                )}
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                {wsWritten > 0
                  ? t.adminDashboard.wsWrittenActive
                  : t.adminDashboard.wsWrittenNone}
              </p>
            </div>

            <h3 className="text-foreground mb-1 flex items-center gap-2 text-sm font-semibold">
              <Rows3 className="h-3.5 w-3.5" />
              {t.adminDashboard.compareTitle}
            </h3>
            <p className="text-muted-foreground mb-3 text-xs">
              {t.adminDashboard.compareHint}
            </p>

            {comparisons.length === 0 ? (
              <p className="text-muted-foreground py-4 text-center text-xs">
                {t.adminDashboard.compareEmpty}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-left">
                      <th className="pb-2 font-normal">
                        {t.adminDashboard.compareTrade}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        PnL {t.adminDashboard.shadowDetailRest}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        PnL {t.adminDashboard.shadowDetailWs}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        Comm. {t.adminDashboard.shadowDetailRest}
                      </th>
                      <th className="pb-2 text-right font-normal">
                        Comm. {t.adminDashboard.shadowDetailWs}
                      </th>
                      <th className="pb-2 text-right font-normal">Qty</th>
                      <th className="pb-2 text-right font-normal"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map((c, i) => (
                      <tr
                        key={`${String(c.externalId)}-${i}`}
                        className="border-border/50 border-b"
                      >
                        <td className="text-foreground py-2">
                          <span className="font-medium">{String(c.symbol)}</span>
                          <span className="text-muted-foreground ml-2 font-mono text-[10px]">
                            {String(c.externalId).split(":").pop()}
                          </span>
                        </td>
                        <td className="text-foreground py-2 text-right font-mono">
                          {c.restPnl === null ? "-" : String(c.restPnl)}
                        </td>
                        <td
                          className={`py-2 text-right font-mono ${
                            c.seenByWs ? "text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          {c.wsPnl === null ? "-" : String(c.wsPnl)}
                        </td>
                        <td className="text-foreground py-2 text-right font-mono">
                          {c.restCommission === null ? "-" : String(c.restCommission)}
                        </td>
                        <td
                          className={`py-2 text-right font-mono ${
                            c.feesVerified
                              ? "text-foreground"
                              : "text-muted-foreground italic"
                          }`}
                          title={
                            c.feesVerified
                              ? undefined
                              : t.adminDashboard.compareFeesUnverifiedHint
                          }
                        >
                          {c.wsCommission === null ? "-" : String(c.wsCommission)}
                          {!c.feesVerified && c.seenByWs ? " *" : ""}
                        </td>
                        <td className="text-muted-foreground py-2 text-right font-mono">
                          {c.restQuantity === null ? "-" : String(c.restQuantity)}
                          {c.seenByWs && c.wsQuantity !== c.restQuantity
                            ? ` / ${String(c.wsQuantity)}`
                            : ""}
                        </td>
                        <td className="py-2 text-right">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] ${
                              !c.seenByWs
                                ? "bg-accent text-muted-foreground"
                                : c.matches
                                  ? "bg-pnlPositive/15 text-pnlPositive"
                                  : "bg-pnlNegative/15 text-pnlNegative"
                            }`}
                          >
                            {!c.seenByWs
                              ? t.adminDashboard.compareNotSeen
                              : c.matches
                                ? t.adminDashboard.compareMatch
                                : t.adminDashboard.compareMismatch}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* L'asterisque doit etre explique : un accord non verifie
                    affiche comme un accord donnerait une confiance infondee. */}
                {comparisons.some((c) => c.seenByWs && !c.feesVerified) && (
                  <p className="text-muted-foreground mt-2 text-[10px] italic">
                    * {t.adminDashboard.compareFeesUnverifiedHint}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* --- Santé WebSocket --- */}
          <div>
            <h3 className="text-foreground mb-3 flex items-center gap-2 text-sm font-semibold">
              <Radio className="h-3.5 w-3.5" />
              {t.adminDashboard.endpointBreakdownSocketTitle}
            </h3>
            <div className="flex flex-wrap gap-6 text-sm">
              <span className="text-muted-foreground">
                {t.adminDashboard.endpointBreakdownSocketEvents}{" "}
                <span className="text-foreground font-mono">
                  {totalWsEvents}
                </span>
              </span>
              <span className="text-muted-foreground">
                {t.adminDashboard.endpointBreakdownSocketReconnects}{" "}
                <span
                  className={
                    (data?.totalWsReconnects ?? 0) > 0
                      ? "text-pnlNegative font-mono"
                      : "text-foreground font-mono"
                  }
                >
                  {data?.totalWsReconnects ?? 0}
                </span>
              </span>
            </div>
            {/* Un socket muet ne consomme aucun quota et ressemblerait à un
                succès total. Le distinguer d'un transport pas encore en service
                est tout l'intérêt de ces deux messages. */}
            <p className="text-muted-foreground mt-2 text-xs">
              {totalWsEvents === 0 && hasBetaTesters
                ? t.adminDashboard.endpointBreakdownSocketSilent
                : totalWsEvents === 0
                  ? t.adminDashboard.endpointBreakdownSocketNotStarted
                  : null}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TradovateEndpointBreakdownCard;
